import { sheets } from '@/app/grid/controller/Sheets';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { embedClampToContent, embedClampToRange, embedInitialViewRange, isEmbed } from '@/app/helpers/isEmbed';
import type { Rectangle } from 'pixi.js';

/**
 * Offset applied when clamping viewport to content bounds to ensure cell
 * borders remain visible at the edge.
 */
export const BORDER_VISIBILITY_OFFSET = 1;

export interface ViewportClampConfig {
  contentBounds: Rectangle | null;
  hasValidBounds: boolean;
  /** False for embed initialView without `clamp`: used-cell bounds are narrower than the framed view. */
  clampVisibleToContentEdges: boolean;
  leftClamp: number;
  topClamp: number;
}

export function getViewportClampConfig(headingSize: { width: number; height: number }): ViewportClampConfig {
  // For embedClampToRange, we only need sheets to exist (they're available before initialized is set).
  // For content-based clamping, we need full initialization to check cursor position.
  const hasSheets = sheets.sheets.length > 0;
  const shouldClamp = !!(
    embedClampToContent &&
    (embedClampToRange ? hasSheets : sheets.initialized && sheets.sheet.isCursorWithinContentBounds())
  );
  const contentBounds = shouldClamp && hasSheets ? sheets.sheet.getClampBounds() : null;
  const hasValidBounds = !!(contentBounds && contentBounds.width > 0 && contentBounds.height > 0);
  const isInitialViewWithoutClamp = isEmbed && !!embedInitialViewRange && !embedClampToRange;
  const clampVisibleToContentEdges = hasValidBounds && !isInitialViewWithoutClamp;

  // Viewport x/y are screen pixels; contentBounds origin is world space — convert before combining.
  const scale = pixiApp.viewport.scale.x;
  const leftClamp =
    hasValidBounds && embedClampToRange && contentBounds
      ? headingSize.width - contentBounds.x * scale
      : headingSize.width;
  const topClamp =
    hasValidBounds && embedClampToRange && contentBounds
      ? headingSize.height - contentBounds.y * scale
      : headingSize.height;

  return { contentBounds, hasValidBounds, clampVisibleToContentEdges, leftClamp, topClamp };
}
